import { fetchWithAuth } from '../../api/backendApi';
import {
  coerceSharedShortcutsPayload,
  coerceShortcutsPayload,
  defaultShortcutsPayload,
  normalizeShortcutsBundle,
  sharedShortcutVersion,
} from './shortcutModel';

export class ShortcutSaveConflict extends Error {
  constructor(message, code) {
    super(message);
    this.name = 'ShortcutSaveConflict';
    this.code = code;
    this.conflict = true;
  }
}

export function shortcutConflictFromError(err) {
  if (err instanceof ShortcutSaveConflict) return err;
  const message = String(err?.message || '');
  const match = message.match(/^API Error \(409\): ([\s\S]*)$/);
  if (!match) return null;
  try {
    const body = JSON.parse(match[1]);
    if (body?.error === 'conflict' || body?.error === 'refresh_required') {
      return new ShortcutSaveConflict(
        body.message || 'These shortcuts were updated by someone else. Reload and review the latest version before saving.',
        body.error
      );
    }
  } catch {
    return null;
  }
  return null;
}

function personalEndpoint(tenantId) {
  const q = new URLSearchParams({ tenantId: String(tenantId) });
  return `/api/v2/users/me/prefs/shortcuts?${q.toString()}`;
}

function sharedEndpoint(tenantId) {
  const q = new URLSearchParams({ tenantId: String(tenantId) });
  return `/api/v2/users/me/prefs/shortcuts/shared?${q.toString()}`;
}

export async function loadUserShortcuts(tenantId) {
  if (!tenantId) {
    return normalizeShortcutsBundle(defaultShortcutsPayload(null), null);
  }
  const res = await fetchWithAuth(personalEndpoint(tenantId), 'GET', null, {
    suppressErrorLog: true,
  });
  return normalizeShortcutsBundle(res?.data || res, tenantId);
}

export async function saveUserShortcuts(tenantId, payload) {
  if (!tenantId) throw new Error('Tenant is required');
  const body = coerceShortcutsPayload(payload, tenantId);
  const res = await fetchWithAuth(personalEndpoint(tenantId), 'PUT', body, {
    suppressErrorLog: true,
  });
  if (res === false || res?.success === false) {
    throw new Error(res?.error || 'Could not save shortcuts');
  }
  return coerceShortcutsPayload(res?.data || body, tenantId);
}

export async function saveSharedShortcuts(tenantId, payload) {
  if (!tenantId) throw new Error('Tenant is required');
  const expectedVersion = sharedShortcutVersion(payload);
  const body = coerceSharedShortcutsPayload(payload, tenantId);
  body.expectedVersion = expectedVersion;
  let res;
  try {
    res = await fetchWithAuth(sharedEndpoint(tenantId), 'PUT', body, {
      suppressErrorLog: true,
    });
  } catch (err) {
    const conflict = shortcutConflictFromError(err);
    if (conflict) throw conflict;
    throw err;
  }
  if (res === false || res?.success === false) {
    const conflict = shortcutConflictFromError(
      new Error(`API Error (409): ${JSON.stringify(res)}`)
    );
    if (res?.error === 'conflict' || res?.error === 'refresh_required') {
      throw new ShortcutSaveConflict(res.message || conflict?.message, res.error);
    }
    throw new Error(res?.error || 'Could not save tenant shortcuts');
  }
  return coerceSharedShortcutsPayload(res?.data || body, tenantId);
}
